import BrushRoundedIcon from '@mui/icons-material/BrushRounded'
import { useMemo } from 'react'
import { useDesignAssets } from '../../hooks/useDesignAssets'
import { MiniLoader } from '../Common'

type Props = {
  workspaceId: string
  designIds: readonly string[] | undefined
  itemTitle: string
  tone?: 'helper' | 'member'
}

export const ChecklistAttachedDesignsSection = ({
  workspaceId,
  designIds,
  itemTitle,
  tone = 'member',
}: Props) => {
  const dedupedIds = useMemo(
    () => Array.from(new Set((designIds ?? []).filter((designId): designId is string => typeof designId === 'string' && designId.trim().length > 0))),
    [designIds],
  )
  const { items, loading } = useDesignAssets(workspaceId)
  const attachedDesigns = items.filter((design) => dedupedIds.includes(design.id))
  const helperTone = tone === 'helper'

  if (dedupedIds.length === 0) return null

  return (
    <section
      className={`rounded-3xl border p-4 ${helperTone ? 'border-[var(--border)]' : 'memberDetailCard'}`}
      style={helperTone ? { backgroundColor: 'color-mix(in srgb, var(--surface) 55%, transparent)' } : undefined}
      data-testid="attached-checklist-designs"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className={helperTone ? 'text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]' : ''}>Attached designs</h3>
          <p className={helperTone ? 'text-xs text-[var(--text)] mt-1' : 'memberDetailHint'}>
            Designs linked to {itemTitle} for reference while doing the work.
          </p>
        </div>
        {loading ? <MiniLoader size="xs" inline label="Loading…" /> : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {attachedDesigns.length > 0 ? attachedDesigns.map((design) => (
          <span
            key={design.id}
            className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium ${
              helperTone
                ? 'border-[var(--border)] bg-[color-mix(in_srgb,var(--surface-elevated)_35%,transparent)] text-[var(--text-strong)]'
                : 'memberChecklistHeroChip memberChecklistHeroChip--design'
            }`}
          >
            <BrushRoundedIcon style={{ fontSize: 16 }} />
            <span>{design.title}</span>
          </span>
        )) : (
          <p className={helperTone ? 'text-xs text-[var(--muted)]' : 'memberDetailHint'}>
            {loading ? 'Loading design references…' : `${dedupedIds.length} design${dedupedIds.length === 1 ? '' : 's'} attached.`}
          </p>
        )}
      </div>
    </section>
  )
}
